import axios from 'axios'

import { BaseService } from './base.service'
import { ResponseWrapper, ErrorWrapper } from './util'
import $store from '../store'
import $router from '../router'

import jwt_decode from 'jwt-decode';

export class AuthService extends BaseService {
  static async integrationLogin(integrationToken) {
    try {
      const response = await this.request().post(`${process.env.VUE_APP_EAD_API_URL}/Marketplace/auth`,
        {
          GrantType: "integration_token",
          IntegrationToken: integrationToken
        });


      var exDate = new Date((new Date()).getTime() + process.env.VUE_APP_REFRESH_TOKEN_TIME); // add a data de expiração

      _setAuthData({
        accessToken: response.data.token,
        refreshToken: response.data.refreshToken,
        exp: (+(exDate))// data de expiração do token
      });

      this.startKeepAlive();

      return new ResponseWrapper({ status: 200, data: { success: true } });
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async makeLogin({ email, password }) {
    try {
      const response = await this.request().post(`${process.env.VUE_APP_EAD_API_URL}/Marketplace/auth`,
        {
          email: email,
          password: password,
          grantType: "password"
        });


      var exDate = new Date((new Date()).getTime() + process.env.VUE_APP_REFRESH_TOKEN_TIME); // add a data de expiração

      _setAuthData({
        accessToken: response.data.token,
        refreshToken: response.data.refreshToken,
        exp: (+(exDate))// data de expiração do token
      });

      this.startKeepAlive();

      return new ResponseWrapper({ status: 200, data: { success: true } });
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async makeLogout() {
    try {
      this.stopKeepAlive();
      $store.dispatch('notification/clearLoop');

      // Desregistra o aparelho do push ANTES do logout no backend e do
      // _resetAuthData: depois dele não há token para provar o dono. Limitado
      // por timeout curto e sem lançar — logout nunca falha por causa do push.
      await _unregisterPushDevice(AuthService.getAccessToken());

      const hasAccessToken = Boolean(AuthService.getAccessToken());
      const response = hasAccessToken
        ? await this.request({ auth: true }).post("Marketplace/auth/logout", {})
        : { data: {} };

      _resetAuthData();

      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async refreshTokens(opts = {}) {
    // Uma renovação por vez para TODO chamador (interceptor, reconexão, boot,
    // reAuthenticate): o backend rotaciona o refresh token, então duas
    // renovações simultâneas com o mesmo token garantem 401 para a perdedora —
    // e o 401 aqui leva ao relogin biométrico ou ao clearToLogin. Quem chega
    // com uma renovação em voo espera a mesma resposta. O single-flight do
    // interceptor (http.init.js) só cobria os 401 entre si; a renovação da
    // reconexão corria por fora junto com o PUT do push e o GET do sino.
    if (!_refreshFlight) {
      const flight = { tornDown: false };
      flight.promise = this.request().post(`${process.env.VUE_APP_EAD_API_URL}/Marketplace/auth`, {
        refreshToken: this.getRefreshToken(),
        grantType: 'refresh_token'
      }).then(response => {
        _setAuthData({
          accessToken: response.data.token,
          refreshToken: response.data.refreshToken
        })
        return response
      }).finally(() => {
        if (_refreshFlight === flight) _refreshFlight = null
      })
      _refreshFlight = flight
    }

    const flight = _refreshFlight
    try {
      const response = await flight.promise
      return new ResponseWrapper(response, response.data.token)
    } catch (error) {
      // Só apaga tokens e redireciona para login em caso de falha de autenticação
      // confirmada (401/403). Erro de rede (offline) não destrói a sessão.
      const isAuthError = error.response && [401, 403].includes(error.response.status);
      // suppressRedirect: o interceptor nativo vai tentar o relogin biométrico
      // antes de desistir. NÃO destrói a sessão aqui (nem tokens, nem user no
      // store, nem o loop de notificação) — senão a recuperação in-place ficava
      // com user={} e a home renderizava em branco. Se o relogin falhar, o
      // próprio interceptor faz o teardown + redirect (clearToLogin).
      // Vale por chamador; com vários sem a flag na mesma renovação, o teardown
      // roda uma vez só.
      if (isAuthError && !opts.suppressRedirect && !flight.tornDown) {
        flight.tornDown = true
        _resetAuthData()
        $store.dispatch('notification/clearLoop')
        AuthService.clearToLogin()
      }

      throw new ErrorWrapper(error)
    }
  }

  static isAccessTokenLocallyValid() {
    const token = this.getAccessToken();
    if (!token) return false;
    try {
      const { exp } = jwt_decode(token);
      return typeof exp === 'number' && (Date.now() / 1000) < exp;
    } catch {
      return false;
    }
  }

  static clearToLogin() {
    // Fire-and-forget e ANTES do reset: o token é capturado aqui, na chamada. A
    // sessão provavelmente já morreu (é por isso que estamos aqui), então o
    // DELETE vai sem o interceptor de refresh — ver push.service.
    _unregisterPushDevice(AuthService.getAccessToken());
    _resetAuthData()
    this.stopKeepAlive()

    // limpa o intervalo
    $store.dispatch('notification/clearLoop')

    $router.push({ name: 'login' }).catch(() => { })
  }

  static async forgotPasswordRequest(payload) {
    try {
      const response = await this.request().post(
        `${process.env.VUE_APP_EAD_API_URL}/Marketplace/auth/forgot-password-request`,
        payload
      );

      return new ResponseWrapper({ status: 200, data: { success: true } }, { a: 1 }); //
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  };

  static async forgotPasswordConfirm(postData) {
    try {
      const response = await this.request().post(`${process.env.VUE_APP_EAD_API_URL}/Marketplace/auth/forgot-password-commit`, postData);

      _setAuthData({
        accessToken: response.data.token,
        refreshToken: response.data.refreshToken
      });

      return new ResponseWrapper({ status: 200, data: { success: true } }, { a: 1 }); //
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  };


  static async checkResetHash(hash) {
    try {
      const response = await this.request().get(`${process.env.VUE_APP_EAD_API_URL}/Marketplace/auth/validate-token/${hash}`);
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  };

  static async getAppIdCourseId() {
    try {
      const response = await this.request({ auth: false }).get(`${process.env.VUE_APP_EAD_API_URL}/courses/${window.location.host}`);

      return new ResponseWrapper(response, response.data)
    } catch (error) {
      //throw new ErrorWrapper(error)
      return new ResponseWrapper();
    }
  }

  /**
   ******************************
   * @METODOS
   ******************************
   */

  static hasRefreshToken() {
    return Boolean(localStorage.getItem('refreshToken'))
  }

  static getRefreshToken() {
    return localStorage.getItem('refreshToken');
  }

  static setRefreshToken(refreshToken) {
    localStorage.setItem('refreshToken', refreshToken)
  }

  static hasAccessToken() {
    return Boolean(sessionStorage.getItem('accessToken'));
  }

  static getAccessToken() {
    return sessionStorage.getItem('accessToken');
  }

  static setAccessToken(accessToken) {
    //ACCESSTOKEN = accessToken;
    sessionStorage.setItem('accessToken', accessToken);

    // seta a autorização
    axios.defaults.headers.common['authorization'] = `bearer ${accessToken}`;
  }

  static getUserData() {
    const accessToken = AuthService.getAccessToken();
    // Cobre null/undefined/'' (antes só pegava '' com `==`, deixando null passar
    // pro jwt_decode).
    if (!accessToken) {
      throw new ErrorWrapper('O usuário não esta autenticado!');
    }
    try {
      return jwt_decode(accessToken);
    } catch {
      // Token malformado/corrompido (ex.: janela do relogin) — não vaza o erro
      // cru do jwt-decode ("Invalid token specified: ...") pra cima/pro toast.
      throw new ErrorWrapper('O usuário não esta autenticado!');
    }
  }

  static _keepAlive;
  static _keepAliveInterval = 10 * 60 * 1000; //10 min
  //static _keepAliveInterval = 10  * 1000;

  static startKeepAlive() {
      this.stopKeepAlive();
      var selfRef = this;
      this._keepAlive = setInterval(function () {
        if (selfRef.hasAccessToken()) {
          selfRef.requestKeepAlive();
        }
      }, this._keepAliveInterval);
  }

  static stopKeepAlive() {
    clearInterval(this._keepAlive);
  }

  static async requestKeepAlive() {
    await this.request({ auth: true }).post(`${process.env.VUE_APP_EAD_API_URL}/marketplace/keep-alive`);
  }
}

// require lazy: o módulo do push importa services que voltam até este arquivo
// (http.init -> auth.service); import estático aqui anteciparia esse ciclo no
// boot. Mesmo padrão do user.js com o store.
// Renovação de token em voo (AuthService.refreshTokens): { promise, tornDown }.
let _refreshFlight = null;

function _unregisterPushDevice(accessToken) {
  try {
    const { unregisterPushDevice } = require('@/services/push/pushRegistration');
    return Promise.resolve(unregisterPushDevice(accessToken)).catch(() => {});
  } catch (e) {
    return Promise.resolve();
  }
}

function _resetAuthData() {
  $store.commit('user/SET_CURRENT_USER', {})
  $store.commit('subscription/RESET')
  $store.commit('enroll/RESET_ENROLLS')
  localStorage.removeItem('ead_cached_user')

  AuthService.setRefreshToken('')
  AuthService.setAccessToken('')
}

function _setAuthData({ accessToken, refreshToken, exp } = {}) {
  AuthService.setRefreshToken(refreshToken)
  AuthService.setAccessToken(accessToken)
}
